import React from "react";
import { useLang } from "@/i18n/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Moon, Sparkles, Mail, Phone, Globe } from "lucide-react";

// Elegant, on-brand empty states for Draft and Archived trips.

export const ArchivedPage = () => (
  <div className="relative grid min-h-[100svh] place-items-center bg-[#F7F4EE] px-6 py-14 text-center" data-testid="archived-page">
    <div className="relative z-10 mx-auto max-w-md animate-fade-up">
      <img src="/maqam-logo.png" alt="Maqam" className="mx-auto mb-8 h-11 w-auto" />
      <span className="mx-auto mb-6 grid h-16 w-16 place-items-center rounded-full bg-[#0A192F] text-[#E9CE7D]">
        <Moon size={26} strokeWidth={1.5} />
      </span>
      <p className="font-arabic text-2xl text-[#BCA054]">الحمد لله</p>
      <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-[#0A192F]" data-testid="archived-title">
        Alhamdulillah, Journey Completed!
      </h1>
      <p className="mt-5 text-[15px] leading-relaxed text-slate-600">
        Our October 2026 Umrah journey has successfully concluded.
      </p>
      <p className="mt-3 text-[15px] leading-relaxed text-slate-600">
        Jazakum Allahu Khairan to everyone who was part of this blessed experience. May Allah accept your Umrah and
        grant you many more opportunities to visit His sacred house.
      </p>

      <div className="maqam-card mt-9 rounded-3xl p-6 text-left" data-testid="archived-contact">
        <h2 className="font-display text-lg font-bold text-[#0A192F]">Interested in joining a future journey?</h2>
        <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
          We'd love to hear from you! Reach out to the Maqam Umrah team for information about upcoming trips.
        </p>
        <ul className="mt-5 space-y-2.5">
          <ContactRow icon={Mail} label="Email" value="info@maqamumrah.ca" href="mailto:info@maqamumrah.ca" testId="archived-email" />
          <ContactRow icon={Phone} label="Toronto" value="416-402-5812" href="tel:+14164025812" testId="archived-phone-toronto" />
          <ContactRow icon={Phone} label="Ottawa" value="613-981-4388" href="tel:+16139814388" testId="archived-phone-ottawa" />
          <ContactRow icon={Globe} label="Website" value="maqamumrah.ca" href="https://maqamumrah.ca" external testId="archived-website" />
        </ul>
      </div>
    </div>
  </div>
);

const ContactRow = ({ icon: Icon, label, value, href, external, testId }) => (
  <li>
    <a
      href={href}
      data-testid={testId}
      {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
      className="flex items-center gap-3 rounded-2xl bg-[#F1F5F9]/70 p-3 transition-colors hover:bg-[#FBF3D8]/70"
    >
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#0A192F] text-[#E9CE7D]">
        <Icon size={15} strokeWidth={1.75} />
      </span>
      <span className="min-w-0">
        <span className="block text-[10px] uppercase tracking-[0.14em] text-[#64748B]">{label}</span>
        <span className="block text-sm font-semibold text-[#0A192F]">{value}</span>
      </span>
    </a>
  </li>
);

export const DraftPage = ({ settings }) => {
  const { L, t } = useLang();
  return (
    <div className="relative grid min-h-[100svh] place-items-center bg-[#F8FAFC] px-6 text-center" data-testid="draft-page">
      <div className="absolute right-5 top-5 z-10">
        <LanguageSwitcher variant="dark" />
      </div>
      <div className="mx-auto max-w-md animate-fade-up">
        <img src="/maqam-logo.png" alt="Maqam" className="mx-auto mb-8 h-11 w-auto" />
        <span className="mx-auto mb-6 grid h-14 w-14 place-items-center rounded-full bg-[#0A192F] text-[#E9CE7D]">
          <Sparkles size={22} strokeWidth={1.6} />
        </span>
        <h1 className="font-display text-3xl font-bold tracking-tight text-[#0A192F]">{t("status.draftTitle")}</h1>
        <p className="mt-5 text-[15px] leading-relaxed text-slate-600">{L(settings?.draftMessage)}</p>
      </div>
    </div>
  );
};
